import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { Container } from 'reactstrap';

import { showModal } from '../actions';
import {
  fetchCollections,
  clearCollections,
  clearCollectionsList,
  setCollectionGroup,
  clearSelectedCollections,
  addSelectedCollection,
  removeSelectedCollection,
  setCollectionsSearchType,
} from '../actions/Collections';
import {
  setCollectionUuid,
  setFilesParams,
} from '../actions/Files';
import { setSearchQueryType } from '../actions/Search';

import CollectionsHeader from '../components/Collections/Header';
import CollectionsFilters from '../components/Collections/Filters';
import CollectionsGrid from '../components/Collections/Grid';
import CollectionsList from '../components/Collections/List';
import ArchiveHeader from '../components/ArchiveHeader';
import CollectionPreview from '../components/CollectionPreview';
import CollectionsMoveModal from '../components/Collections/MoveModalV2';
import CustomDragLayer from '../components/CustomDragLayer';
import TrashHeader from '../components/TrashHeader';
import EmptyCollection from './EmptyContainers/Collection';
import MiniHeader from '../mini/components/Header';
import CollectionsMiniGrid from '../mini/components/Collections/Grid';
import MiniEmptyCollection from '../mini/components/EmptyCollection';

import {
  isArchive,
  isMini,
  isTrash,
  resizeSplitContentContainer,
} from '../helpers/App';
import { isMyCollectionsPage, getCollectionsView } from '../helpers/Collections';
import ItemTypes from '../helpers/DNDItemTypes';
import { getDefaultSortParams } from '../helpers/Sort';

import Loading from '../components/Loading';

import SelectionArea from '../lib/viselect/viselect';

class Collections extends Component {
  constructor(props) {
    super(props);

    this.ds = null;

    this.loadCollections = this.loadCollections.bind(this);
    this.clearCollections = this.clearCollections.bind(this);
    this.handleMoveSelectedItems = this.handleMoveSelectedItems.bind(this);
  }

  componentDidMount() {
    const { dispatch } = this.props;
    resizeSplitContentContainer();
    dispatch(setCollectionsSearchType('new'));
    this.clearCollections();
    this.setInitialOrder();
  }

  componentDidUpdate(prevProps) {
    const {
      dispatch,
      match,
      search,
      collectionGroupId,
    } = this.props;

    if (!this.ds && !isMini()) {
      this.ds = new SelectionArea({
        selectables: [
          '.collections .collections-item',
          'table.collections-list tr',
        ],
        startareas: ['.collections'],
        boundaries: ['.main-content .container-fluid'],
        container: '.draggable-container > div',
        behaviour: {
          overlap: 'keep',
        },
        features: {
          touch: false,
          singleTap: {
            allow: false,
          },
        },
      })
        .on('beforestart', ({ event }) => {
          if (
            event.target.closest('.collections-item') ||
            event.target.closest('.list-item')
          ) {
            return false;
          }
          return true;
        })
        .on('start', ({ event }) => {
          if (!event.metaKey) {
            dispatch(clearSelectedCollections());
          }
        })
        .on('move', ({ store: { changed: { added, removed } } }) => {
          added.forEach((s) => {
            const collection = this.getCollectionById(s.id);
            if (collection) {
              dispatch(addSelectedCollection(collection));
            }
          });
          removed.forEach((s) => {
            dispatch(removeSelectedCollection(s.id));
          });
        })
        .on('stop', () => {
          this.ds.clearSelection();
        });
    }

    if (
      match.path !== prevProps.match.path ||
      search !== prevProps.search
    ) {
      if (search !== prevProps.search &&
        (
          (search && !prevProps.search) ||
          (search && prevProps.search)
        )
      ) {
        this.clearCollections(true);
      } else {
        this.clearCollections();
      }
    }

    if (collectionGroupId !== prevProps.collectionGroupId && collectionGroupId !== 0) {
      this.clearCollectionsOrder();
      this.loadCollections();
    }

    const urlParams = this.getUrlParams();
    if (urlParams.length > 0) {
      const prevUrlParams = prevProps.location.pathname.replace(/^[/]+|[/]+$/g, '').split('/');
      if (prevUrlParams.length > 0) {
        if (urlParams[0] !== prevUrlParams[0] && ['collections', 'archive'].includes(urlParams[0])) {
          this.clearCollections();
        }
      }
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(clearSelectedCollections());
    dispatch(setCollectionGroup(0));
    if (this.ds) {
      this.ds.destroy();
    }
  }

  getCollectionById(id) {
    const { collections } = this.props;
    return collections.find(c => (c.uuid === id));
  }

  getUrlParams() {
    const { location } = this.props;
    return location.pathname.replace(/^[/]+|[/]+$/g, '').split('/');
  }

  setInitialOrder() {
    const { dispatch } = this.props;
    const sortParams = getDefaultSortParams('collections', 'position', 'asc');
    dispatch(setFilesParams({
      orderby: sortParams.orderby,
      order: sortParams.order,
    }));
  }

  setCollectionGroupId() {
    const { dispatch } = this.props;

    if (this.isCollectionsPage()) {
      dispatch(setCollectionGroup(-2));
      return;
    }

    if (this.isAllCollectionsPage()) {
      dispatch(setCollectionGroup(0));
      return;
    }

    if (isMyCollectionsPage()) {
      dispatch(setCollectionGroup(-1));
    }
  }

  isCollectionsPage() {
    const { match } = this.props;
    return match.path === '/collections' || match.path === '/archive/collections';
  }

  isAllCollectionsPage() {
    const { match } = this.props;
    return match.path === '/collections/all' || match.path === '/archive/collections/all';
  }

  loadCollections() {
    const { dispatch } = this.props;

    if (this.isAllCollectionsPage() || isMyCollectionsPage() || this.isCollectionsPage()) {
      dispatch(fetchCollections({ my: isMyCollectionsPage() }));
    }
  }

  clearCollections(reload = false) {
    const { dispatch } = this.props;

    dispatch(setSearchQueryType(null));
    dispatch(clearCollectionsList());
    dispatch(clearSelectedCollections());
    this.setCollectionGroupId();
    dispatch(setCollectionUuid(null));
    if (reload) {
      dispatch(fetchCollections({ my: isMyCollectionsPage() }));
    }
  }

  clearCollectionsOrder() {
    const { dispatch } = this.props;

    const sortParams = getDefaultSortParams('collections', 'position', 'asc');
    dispatch(clearCollections(sortParams));
  }

  handleMoveSelectedItems(targetCollectionUuid, droppedItem) {
    const {
      dispatch,
      selectedCollections,
    } = this.props;

    const collections = selectedCollections.map(sc => (sc.uuid));
    if (collections.length === 0 && droppedItem.type === ItemTypes.COLLECTION) {
      collections.push(droppedItem.id);
    }

    dispatch(showModal('MoveItemsModal', {
      className: 'modal-move-items',
      targetCollectionUuid,
      collections,
      backdrop: false,
    }));
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleShowDuplicateModal() {
    const { dispatch, selectedCollections } = this.props;

    dispatch(showModal('DuplicateModal', {
      className: 'modal-duplicate-collections',
      collections: selectedCollections,
    }));
  }

  render() {
    const {
      collections,
      collectionsLoading,
      collectionsEnd,
      selectedCollections,
      sidebarCollectionOpen,
      collectionMoveModal,
      moveModalCollection,
    } = this.props;

    const collectionsView = getCollectionsView();

    let moveModalCollections = [];
    if (selectedCollections.length > 0) {
      moveModalCollections = selectedCollections;
    } else if (moveModalCollection) {
      moveModalCollections = [moveModalCollection];
    }

    return (
      <div className={classnames('main-content', 'split-content', 'page-collections', {
        'split-content-active': sidebarCollectionOpen && !isArchive(),
        'archive-content': isArchive(),
        'trash-content': isTrash(),
      })}
      >
        {!isMini() && (
          <>
            <ArchiveHeader />
            <TrashHeader />
          </>
        )}
        <div className="split-content-wrapper">
          <Container fluid className="draggable-container">
            {isMini() ? (
              <MiniHeader />
            ) : (
              <>
                <CollectionsHeader
                  collections={collections}
                  refreshCollections={this.clearCollections}
                  loading={collectionsLoading}
                  isAllCollections={this.isAllCollectionsPage()}
                  isMyCollections={isMyCollectionsPage()}
                />
                <CollectionsFilters />
              </>
            )}
            {(collections.length === 0 && collectionsEnd) && (
              isMini() ? (
                <MiniEmptyCollection
                  allCollections={this.isCollectionsPage()}
                />
              ) : (
                <EmptyCollection
                  asset="collection"
                  allCollections={this.isCollectionsPage()}
                  myCollections={isMyCollectionsPage()}
                />
              )
            )}
            <>
              {isMini() ? (
                <CollectionsMiniGrid
                  collections={collections}
                />
              ) : (
                <>
                  {['grid', 'framed'].includes(collectionsView) && (
                    <CollectionsGrid
                      collections={collections}
                      handleMoveSelectedItems={this.handleMoveSelectedItems}
                      selectedView={collectionsView}
                    />
                  )}
                  {collectionsView === 'list' && (
                    <CollectionsList
                      collections={collections}
                      handleMoveSelectedItems={this.handleMoveSelectedItems}
                    />
                  )}
                </>
              )}
            </>
            {collectionsLoading ? (
              <Loading />
            ) : (
              !collectionsEnd && (
                <Waypoint
                  bottomOffset="-300px"
                  onEnter={this.loadCollections}
                />
              )
            )}
          </Container>
          <CollectionPreview />
          <CustomDragLayer />
        </div>
        {collectionMoveModal && (
          <CollectionsMoveModal collections={moveModalCollections} />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  collectionGroupId: state.Collections.collectionGroupId,
  collections: state.Collections.collections,
  selectedCollections: state.Collections.selectedCollections,
  collectionsLoading: state.Collections.collectionsLoading,
  collectionsEnd: state.Collections.collectionsEnd,
  search: state.Search.search,
  sidebarCollectionOpen: state.Collections.sidebarCollectionOpen,
  collectionMoveModal: state.Collections.moveModalOpen,
  moveModalCollection: state.Collections.moveModalCollection,
});

Collections.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  collectionGroupId: PropTypes.number,
  collections: PropTypes.array.isRequired,
  selectedCollections: PropTypes.array.isRequired,
  collectionsLoading: PropTypes.bool.isRequired,
  collectionsEnd: PropTypes.bool.isRequired,
  search: PropTypes.string,
  sidebarCollectionOpen: PropTypes.bool.isRequired,
  collectionMoveModal: PropTypes.bool.isRequired,
  moveModalCollection: PropTypes.object,
};

Collections.defaultProps = {
  collectionGroupId: null,
  moveModalCollection: null,
  search: null,
};

export default withRouter(connect(mapStateToProps)(Collections));
